/**
 * Voo e stamina de um atleta num tick (item 4.1). Só + - * / e Math.sqrt: o resultado é igual em
 * qualquer navegador e no servidor.
 */
import { FLIGHT, INPUT_AXIS_MAX, PITCH, byStat, type FlightStats, type Position } from "@besenliga/shared";
import { BUTTON, NO_INPUT, type InputCommand } from "./input.js";

export type Team = "casa" | "fora";

export interface Vec3 {
  x: number;
  y: number;
  z: number;
}

/** Direção para onde a vassoura aponta, no plano do chão (vetor unitário). */
export interface Heading {
  x: number;
  z: number;
}

export interface AthleteState {
  id: string;
  team: Team;
  role: Position;
  position: Vec3;
  velocity: Vec3;
  heading: Heading;
  /** De 0 (esgotado) a 1 (cheio). */
  stamina: number;
  /** Esgotou e ainda não recuperou o suficiente para usar o turbo. */
  exhausted: boolean;
  /** Turbo ligado neste tick (para a animação e o som). */
  turbo: boolean;
  stats: FlightStats;
  /** Ticks restantes de atordoamento (sem controle). */
  stunTicks: number;
  /** Botões do tick anterior, para saber quando um botão foi apertado ou solto. */
  buttons: number;
  /** Ticks segurando o botão de carregar (arremesso ou rebatida forte). */
  chargeTicks: number;
  /** Ticks até poder usar a habilidade de novo (item 4.4). */
  abilityCooldown: number;
  /** Ticks que restam do efeito da habilidade em curso (0 = nenhuma). */
  abilityTicks: number;
}

/** Números de voo do atleta, já resolvidos pelos atributos. */
export interface FlightParams {
  maxSpeed: number;
  /** Giro máximo da vassoura (rad/s). */
  turnRate: number;
  acceleration: number;
  turnAcceleration: number;
  staminaDrain: number;
  staminaRegen: number;
  mass: number;
}

export function flightParams(stats: FlightStats): FlightParams {
  return {
    maxSpeed: byStat(FLIGHT.maxSpeed, stats.velocidade),
    turnRate: byStat(FLIGHT.turnRate, stats.controle),
    acceleration: byStat(FLIGHT.acceleration, stats.aceleracao),
    turnAcceleration: byStat(FLIGHT.turnAcceleration, stats.controle),
    staminaDrain: byStat(FLIGHT.staminaDrain, stats.stamina),
    staminaRegen: byStat(FLIGHT.staminaRegen, stats.stamina),
    mass: byStat(FLIGHT.mass, stats.estabilidade),
  };
}

const clamp = (v: number, lo: number, hi: number) => (v < lo ? lo : v > hi ? hi : v);

/**
 * Gira o vetor unitário `h` em direção a `d` (também unitário) no máximo `angle` radianos.
 * Seno e cosseno do passo vêm de série de Taylor (só + - * /), iguais em qualquer máquina.
 */
export function rotateToward(h: Heading, d: Heading, angle: number): Heading {
  const dot = h.x * d.x + h.z * d.z;
  const a2 = angle * angle;
  const c = 1 - a2 / 2 + (a2 * a2) / 24;
  if (dot >= c) return { x: d.x, z: d.z };
  const sn = angle - (angle * a2) / 6 + (angle * a2 * a2) / 120;
  // Produto vetorial negativo: o destino está no sentido de guinada positiva (para a esquerda do atleta).
  const cross = h.x * d.z - h.z * d.x;
  const s = cross <= 0 ? sn : -sn;
  const x = h.x * c + h.z * s;
  const z = h.z * c - h.x * s;
  const len = Math.sqrt(x * x + z * z);
  return { x: x / len, z: z / len };
}

/**
 * Avança o voo de um atleta. O comando diz para onde ir no mundo (x, z) e se sobe ou desce (y);
 * a câmera é do cliente, que converte o direcional antes de mandar o comando.
 *
 * A vassoura gira com velocidade limitada (FLIGHT.turnRate) e só empurra para onde aponta: pedir o sentido
 * oposto faz o atleta frear e dar meia-volta numa curva, sem trocar de direção de uma vez.
 */
export function flyAthlete(a: AthleteState, command: InputCommand, dt: number): AthleteState {
  const p = flightParams(a.stats);
  const stunned = a.stunTicks > 0;
  const cmd = stunned ? NO_INPUT : command;

  // Direção pedida, com módulo de no máximo 1.
  let dx = cmd.x / INPUT_AXIS_MAX;
  let dz = cmd.z / INPUT_AXIS_MAX;
  let dLen = Math.sqrt(dx * dx + dz * dz);
  if (dLen > 1) {
    dx /= dLen;
    dz /= dLen;
    dLen = 1;
  }
  const wants = dLen > 0.05;

  // Stamina e turbo.
  const turboPressed = (cmd.buttons & BUTTON.turbo) !== 0 && wants;
  const turbo = turboPressed && !a.exhausted && a.stamina > 0;
  let stamina = turbo ? a.stamina - p.staminaDrain * dt : a.stamina + p.staminaRegen * dt;
  stamina = clamp(stamina, 0, 1);
  const exhausted = stamina <= 0 ? true : a.exhausted && stamina < FLIGHT.staminaRecoverAt;

  const maxSpeed = p.maxSpeed * (turbo ? FLIGHT.turboSpeedFactor : 1);
  const accel = p.acceleration * (turbo ? FLIGHT.turboAccelerationFactor : 1);

  // A vassoura gira em direção ao pedido.
  const heading = wants
    ? rotateToward(a.heading, { x: dx / dLen, z: dz / dLen }, p.turnRate * dt)
    : a.heading;
  const align = wants ? clamp(heading.x * (dx / dLen) + heading.z * (dz / dLen), 0, 1) : 0;
  const targetSpeed = maxSpeed * dLen * align;

  // Velocidade horizontal: na direção da vassoura acelera ou freia; de lado, a deriva é cortada pelo Controle.
  const vx = a.velocity.x;
  const vz = a.velocity.z;
  const ex = heading.x * targetSpeed - vx;
  const ez = heading.z * targetSpeed - vz;
  const along = ex * heading.x + ez * heading.z;
  const lx = ex - along * heading.x;
  const lz = ez - along * heading.z;
  const alongCap = (along >= 0 && wants ? accel : FLIGHT.brakeAcceleration) * dt;
  const alongStep = clamp(along, -alongCap, alongCap);
  const lateral = Math.sqrt(lx * lx + lz * lz);
  const lateralCap = p.turnAcceleration * dt;
  const lateralScale = lateral > lateralCap ? lateralCap / lateral : 1;
  const nvx = vx + alongStep * heading.x + lx * lateralScale;
  const nvz = vz + alongStep * heading.z + lz * lateralScale;

  // Vertical.
  const targetVy = (cmd.y / INPUT_AXIS_MAX) * FLIGHT.climbSpeed;
  const vyCap = FLIGHT.climbAcceleration * dt;
  const nvy = a.velocity.y + clamp(targetVy - a.velocity.y, -vyCap, vyCap);

  const position = {
    x: a.position.x + nvx * dt,
    y: a.position.y + nvy * dt,
    z: a.position.z + nvz * dt,
  };
  return {
    ...a,
    position,
    velocity: { x: nvx, y: nvy, z: nvz },
    heading,
    stamina,
    exhausted,
    turbo,
    stunTicks: stunned ? a.stunTicks - 1 : 0,
  };
}

/** Teto de voo menos o raio do atleta. */
export const FLIGHT_CEILING = PITCH.ceiling - FLIGHT.athleteRadius;
